// One election, as reported.
//
// The two things this component must not do, both of them ways of turning a
// lawful outcome into a wrong one:
//
//  • **A shortfall is not a withheld decision.** `outcome: 'shortfall'` is
//    `status: 'ok'` — fewer places filled than declared, because too few
//    candidates stood or because No Confidence outpolled them, and Standing
//    Committee appoints to the rest (SO 15.12). It gets an `.alert`, a note
//    beside a result. `outcome: 'tie-unresolved'` gets the verdict slot standing
//    empty, because there is no result. The gate below is on `outcome`, never on
//    `unfilled > 0` — an unresolved tie also leaves places unfilled, and gating
//    on the number would print "Standing Committee appoints" over a place that
//    is still Synod's to fill.
//  • **The runner-up is never elected.** Whatever the reason for the shortfall,
//    nobody moves up. That sentence is in the alert in bold, because it is the
//    single most likely thing for a well-meaning person to get wrong in the room.

import { useId, type JSX } from 'react';
import type {
  CountIssue,
  CountingMethodId,
  Electorate,
  PositionItemResult,
} from '@cca/decides-count';
import { CandidateTable } from './CandidateTable';
import { IssueList } from './IssueList';
import { TieWithheldCard } from './WithheldCard';
import { plural, unresolvedTies, type PositionResultView } from './view';

/** SO 15.8 — who may MARK this position. A per-position property, never a
 *  per-ballot chamber rule (types.ts:25-31 names conflating the two as the
 *  likeliest modelling error in the platform). */
const ELECTORATE_COPY: Record<Electorate, string> = {
  clergy: 'Only members of the House of Clergy could vote for this position (SO 15.8).',
  laity: 'Only members of the House of Laity could vote for this position (SO 15.8).',
  both: 'Every member of Synod could vote for this position (SO 15.8).',
};

/** The `stv` sentence is not a description of a count that ran. STV is
 *  REGISTERED and not implemented: it fails closed at validation with a fatal
 *  (types.ts:79-81), so no counted result can carry it, and a line here reading
 *  "Counted by single transferable vote" would be the one false statement on the
 *  screen if one ever did. */
export const METHOD_COPY: Record<CountingMethodId, string> = {
  fptp: 'Counted first past the post.',
  approval: 'Counted by approval — a paper may mark as many candidates as it likes.',
  stv: 'Single transferable vote is not built, so nothing may be declared for this position.',
};

interface PositionResultProps {
  view: PositionResultView;
  itemNumber: number;
  itemCount: number;
  issues: CountIssue[];
}

export function PositionResult({
  view,
  itemNumber,
  itemCount,
  issues,
}: PositionResultProps): JSX.Element {
  const { result } = view;
  const titleId = useId();
  const ties = unresolvedTies(result);

  // Every unfilled place accounted for by an unresolved tie means nothing else
  // on this item is unsettled — which is worth saying out loud, because the card
  // above otherwise reads as though the whole election were in doubt.
  const tiedSeats = ties.reduce((total, tie) => total + tie.seatsAvailable, 0);
  const everythingElseSettled = ties.length === 1 && tiedSeats === result.unfilled;

  return (
    <section className="card result-item" aria-labelledby={titleId}>
      <p className="eyebrow result-eyebrow">
        Item {itemNumber} of {itemCount} · Election
      </p>
      <h2 className="result-item-title" id={titleId}>
        {view.title}
      </h2>

      <p className="result-scope">
        {plural(result.positions, 'place', 'places')} on this ballot
        {result.alternateSeats > 0
          ? `: ${plural(result.seats, 'member', 'members')} and ${plural(result.alternateSeats, 'alternate', 'alternates')}. Under Standing Order 15.10 the alternate is the lowest-polling of those elected.`
          : '.'}{' '}
        {ELECTORATE_COPY[result.electorate]} {METHOD_COPY[result.method]}
      </p>

      {ties.map((tie) => (
        <TieWithheldCard
          key={`${tie.at}-${String(tie.votes)}`}
          result={result}
          tie={tie}
          candidateNames={view.candidateNames}
          everythingElseSettled={everythingElseSettled}
        />
      ))}

      <h3 className="result-subheading">How the votes fell</h3>
      <CandidateTable result={result} candidateNames={view.candidateNames} />

      {result.outcome === 'shortfall' ? <Shortfall result={result} issues={issues} /> : null}
      {result.outcome === 'filled' ? (
        <p className="result-verdict result-verdict-carried">
          {plural(result.positions, 'place is', 'places are')} filled.
        </p>
      ) : null}

      <h3 className="result-subheading">How the papers were accounted for</h3>
      <PaperTallies result={result} />

      <IssueList issues={issues} heading="What the count reported for this item" level={3} />
    </section>
  );
}

/** SO 15.12 — a lawful outcome, reported as one. */
function Shortfall({
  result,
  issues,
}: {
  result: PositionItemResult;
  issues: CountIssue[];
}): JSX.Element {
  // Read from the ENGINE's own report rather than re-deriving why the places are
  // empty: `no-confidence-carried` is raised by the allocation walk itself, and
  // a second guess at the same fact here is a second place to be wrong.
  const noConfidence = issues.some((issue) => issue.code === 'no-confidence-carried');
  const count =
    result.positions === 1
      ? 'The place is not filled.'
      : `${String(result.unfilled)} of the ${String(result.positions)} places ${result.unfilled === 1 ? 'is' : 'are'} not filled.`;

  return (
    <p className="alert alert-warn result-alert">
      {noConfidence ? `No Confidence outpolled the remaining candidates. ${count}` : count} Under
      Standing Order 15.12 {result.unfilled === 1 ? 'it goes' : 'they go'} to Standing Committee to
      appoint. <strong>The next-highest candidate is not elected.</strong>
    </p>
  );
}

/**
 * Every paper, including the ones that could not be counted.
 *
 * Unlike the "did not vote" row on a motion, a 0 here is not withheld and is not
 * hidden: it is a genuine count of a real question ("how many papers marked more
 * candidates than there are places?"), and the engine reports each of these
 * precisely so that "a silent drop still counts correctly and nobody ever learns
 * a bad mark arrived" cannot happen (types.ts:531-533). A screen that dropped
 * the zero rows would put that back.
 */
function PaperTallies({ result }: { result: PositionItemResult }): JSX.Element {
  // Labelled with the paper's own words where the paper has one — "Abstain", as
  // VOTE_OPTIONS has it and as the House tally now does. One vocabulary across
  // both screens and all four tables.
  const rows: { label: string; value: number }[] = [
    { label: 'Papers counted', value: result.papers.counted },
    { label: 'Abstain', value: result.papers.abstain },
    { label: 'Blank papers', value: result.papers.blank },
    { label: 'Marked No Confidence', value: result.papers.noConfidence },
    { label: 'Rejected — more marks than places', value: result.papers.overVote },
    { label: 'Repeated marks, counted once', value: result.papers.duplicateMarks },
    { label: 'Excluded — not entitled to vote here', value: result.papers.ineligible },
  ];

  return (
    <table className="table result-table result-papers">
      <caption className="result-table-caption">
        Every paper is accounted for, including the ones that could not be counted.
      </caption>
      <thead>
        <tr>
          <th scope="col">
            <span className="ballot-sr">What was on the paper</span>
          </th>
          <th scope="col" className="num">
            Papers
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <th scope="row">{row.label}</th>
            <td className="num">{row.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
